import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import {
  CATALOG_COMPONENT_DETAIL_QUERY_KEY,
} from '@/shared/hooks/api/catalog'
import { OPEN_ROHS_PARTS_QUERY_KEY } from '@/shared/hooks/api/openRohsParts'

export const PRODUCT_COMPLIANCE_FILES_QUERY_KEY =
  'productComplianceFiles' as const

export type ComplianceFileStatus = 'ACTIVE' | 'IDLE'

export type ComplianceActor = 'OEM' | 'SUPPLIER'

export type RohsProductComplianceFileView = {
  id: string
  status: ComplianceFileStatus
  completedAt: string | null
  completedByActor: ComplianceActor | null
  latestOutputDocumentId: string | null
  latestDeclarationNumber: string | null
  createdAt: string
  updatedAt: string
}

export type RohsProductComplianceFileOutputDocumentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'

export type RohsProductComplianceFileOutputDocument = {
  id: string
  rohsProductComplianceFileId: string
  documentType: string
  declarationNumber: string | null
  fileName: string
  fileSizeKb: number | null
  contentType: string | null
  createdByActor: ComplianceActor | null
  createdByUserId: string | null
  createdByUserDisplayName?: string
  status: RohsProductComplianceFileOutputDocumentStatus
  errorMessage: string | null
  statusUpdatedAt: string | null
  workflowRunId: string | null
  createdAt: string
  updatedAt: string
}

export type RohsComponentComplianceFileView = {
  id: string | null
  componentId: string
  sku: string
  componentName: string | null
  supplierId: string
  supplierName: string
  complianceActor: ComplianceActor
  status: ComplianceFileStatus | null
  completedAt: string | null
  completedByActor: ComplianceActor | null
}

export type RohsComponentEvidenceSlot =
  | 'TEST_REPORT'
  | 'DRAWINGS_SCHEMAS'

export type RohsComponentComplianceFileSlotView = {
  slotKind: RohsComponentEvidenceSlot
  fileName: string | null
  uploadedAt: string | null
  uploadedByActor: ComplianceActor | null
}

export type RohsTestReportAuditVerdict =
  | 'PASS'
  | 'SUBJECT_MISMATCH'
  | 'MISSING_SUBSTANCES'
  | 'OVER_LIMIT'
  | 'NOT_A_ROHS_TEST_REPORT'
  | 'INCONCLUSIVE'
  | 'NO_TEST_REPORT'

export type RohsTestReportAuditTrigger = 'manual' | 'upload'

export type RohsTestReportAuditUploadedByActor = 'OEM' | 'CATALOG_SUPPLIER'

export type RohsTestReportAuditStoredEntry = {
  verdict: RohsTestReportAuditVerdict
  reasoningEn: string
  reasoningZh: string
  attachmentId: string | null
  fileName: string | null
  subjectMatch: boolean | null
  missingSubstanceCodes: string[]
  overLimitMaterials: {
    substanceCode: string
    materialName: string | null
    valueMgKg: number
    limitMgKg: number
  }[]
  timestamp: string
  runByUserId: string | null
  trigger: RohsTestReportAuditTrigger
  uploadedByActor: RohsTestReportAuditUploadedByActor | null
  inconclusiveReason?: string | null
}

export type RohsComponentComplianceFileDetailView = {
  id: string
  componentId: string
  sku: string
  componentName: string | null
  supplierName: string
  complianceActor: ComplianceActor | null
  status: ComplianceFileStatus
  completedAt: string | null
  completedByActor: ComplianceActor | null
  slots: RohsComponentComplianceFileSlotView[]
  latestTestReportAudit?: RohsTestReportAuditStoredEntry | null
  testReportAuditHistory?: RohsTestReportAuditStoredEntry[]
}

export const COMPONENT_COMPLIANCE_FILE_QUERY_KEY =
  'componentComplianceFile' as const

export type RohsProductComplianceFilesState = {
  product: RohsProductComplianceFileView | null
  components: RohsComponentComplianceFileView[]
  outputDocuments: RohsProductComplianceFileOutputDocument[]
}

export function useGetRohsProductComplianceFiles(
  productId: string | undefined,
  options?: {
    enabled?: boolean
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data?: RohsProductComplianceFilesState }
        }) => number | false | undefined)
  }
) {
  return useQuery<RohsProductComplianceFilesState, Error>({
    queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<RohsProductComplianceFilesState>(
        `/v2/products/${productId}/rohs/compliance-file`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) && productId != null && productId.length > 0,
    refetchInterval: options?.refetchInterval,
  })
}

export type CompleteRohsProductComplianceFileResult = {
  product: RohsProductComplianceFileView
  warnings: string[]
}

export type UncompleteRohsProductComplianceFileResult = {
  product: RohsProductComplianceFileView
}

export function useCompleteRohsProductComplianceFile(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<CompleteRohsProductComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<CompleteRohsProductComplianceFileResult>(
        `/v2/products/${productId}/rohs/compliance-file/complete`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useUncompleteRohsProductComplianceFile(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<UncompleteRohsProductComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response =
        await apiClient.post<UncompleteRohsProductComplianceFileResult>(
          `/v2/products/${productId}/rohs/compliance-file/uncomplete`
        )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useGetRohsComponentComplianceFile(
  productId: string | undefined,
  componentId: string | undefined,
  options?: {
    enabled?: boolean
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data?: RohsComponentComplianceFileDetailView }
        }) => number | false | undefined)
  }
) {
  return useQuery<RohsComponentComplianceFileDetailView, Error>({
    queryKey: [
      COMPONENT_COMPLIANCE_FILE_QUERY_KEY,
      productId,
      componentId,
    ],
    queryFn: async () => {
      const response = await apiClient.get<RohsComponentComplianceFileDetailView>(
        `/v2/products/${productId}/components/${componentId}/rohs/compliance-file`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) &&
      productId != null &&
      productId.length > 0 &&
      componentId != null &&
      componentId.length > 0,
    refetchInterval: options?.refetchInterval,
  })
}

const ROHS_TEST_REPORT_AUDIT_REQUEST_TIMEOUT_MS = 180_000

export function useRunRohsComponentTestReportAudit(
  productId: string,
  componentId: string
) {
  const queryClient = useQueryClient()

  return useMutation<RohsTestReportAuditStoredEntry, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<RohsTestReportAuditStoredEntry>(
        `/v2/products/${productId}/components/${componentId}/rohs/compliance-file/test-report-audit`,
        undefined,
        { timeout: ROHS_TEST_REPORT_AUDIT_REQUEST_TIMEOUT_MS }
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          COMPONENT_COMPLIANCE_FILE_QUERY_KEY,
          productId,
          componentId,
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useUploadComponentComplianceSlot(
  productId: string,
  componentId: string
) {
  const queryClient = useQueryClient()

  return useMutation<
    RohsComponentComplianceFileDetailView,
    Error,
    { slotKind: RohsComponentEvidenceSlot; file: File }
  >({
    mutationFn: async ({ slotKind, file }) => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post<RohsComponentComplianceFileDetailView>(
        `/v2/products/${productId}/components/${componentId}/rohs/compliance-file/slots/${slotKind}`,
        formData
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          COMPONENT_COMPLIANCE_FILE_QUERY_KEY,
          productId,
          componentId,
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
      void queryClient.invalidateQueries({
        queryKey: [OPEN_ROHS_PARTS_QUERY_KEY, productId],
      })
    },
  })
}

export type CompleteRohsComponentComplianceFileResult = {
  file: RohsComponentComplianceFileDetailView
  warnings: string[]
}

export function useCompleteRohsComponentComplianceFile(
  productId: string,
  componentId: string
) {
  const queryClient = useQueryClient()

  return useMutation<CompleteRohsComponentComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response =
        await apiClient.post<CompleteRohsComponentComplianceFileResult>(
          `/v2/products/${productId}/components/${componentId}/rohs/compliance-file/complete`
        )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          COMPONENT_COMPLIANCE_FILE_QUERY_KEY,
          productId,
          componentId,
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
      void queryClient.invalidateQueries({
        queryKey: [OPEN_ROHS_PARTS_QUERY_KEY, productId],
      })
    },
  })
}

export function useUncompleteRohsComponentComplianceFile(
  productId: string,
  componentId: string
) {
  const queryClient = useQueryClient()

  return useMutation<
    { file: RohsComponentComplianceFileDetailView },
    Error,
    void
  >({
    mutationFn: async () => {
      const response = await apiClient.post<{
        file: RohsComponentComplianceFileDetailView
      }>(
        `/v2/products/${productId}/components/${componentId}/rohs/compliance-file/uncomplete`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          COMPONENT_COMPLIANCE_FILE_QUERY_KEY,
          productId,
          componentId,
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
      void queryClient.invalidateQueries({
        queryKey: [OPEN_ROHS_PARTS_QUERY_KEY, productId],
      })
    },
  })
}

function invalidateCatalogComponentComplianceQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  componentId: string
) {
  void queryClient.invalidateQueries({
    queryKey: [COMPONENT_COMPLIANCE_FILE_QUERY_KEY, 'catalog', componentId],
  })
  void queryClient.invalidateQueries({
    queryKey: [CATALOG_COMPONENT_DETAIL_QUERY_KEY, componentId],
  })
  void queryClient.invalidateQueries({
    queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY],
  })
  void queryClient.invalidateQueries({
    queryKey: [OPEN_ROHS_PARTS_QUERY_KEY],
  })
}

export function useGetCatalogRohsComponentComplianceFile(
  componentId: string | undefined,
  options?: {
    enabled?: boolean
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data?: RohsComponentComplianceFileDetailView }
        }) => number | false | undefined)
  }
) {
  return useQuery<RohsComponentComplianceFileDetailView, Error>({
    queryKey: [COMPONENT_COMPLIANCE_FILE_QUERY_KEY, 'catalog', componentId],
    queryFn: async () => {
      const response = await apiClient.get<RohsComponentComplianceFileDetailView>(
        `/v1/catalog/components/${componentId}/rohs/compliance-file`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) &&
      componentId != null &&
      componentId.length > 0,
    refetchInterval: options?.refetchInterval,
  })
}

export function useRunCatalogRohsComponentTestReportAudit(
  componentId: string
) {
  const queryClient = useQueryClient()

  return useMutation<RohsTestReportAuditStoredEntry, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<RohsTestReportAuditStoredEntry>(
        `/v1/catalog/components/${componentId}/rohs/compliance-file/test-report-audit`,
        undefined,
        { timeout: ROHS_TEST_REPORT_AUDIT_REQUEST_TIMEOUT_MS }
      )
      return response.data
    },
    onSuccess: () => {
      invalidateCatalogComponentComplianceQueries(queryClient, componentId)
    },
  })
}

export function useUploadCatalogComponentComplianceSlot(componentId: string) {
  const queryClient = useQueryClient()

  return useMutation<
    RohsComponentComplianceFileDetailView,
    Error,
    { slotKind: RohsComponentEvidenceSlot; file: File }
  >({
    mutationFn: async ({ slotKind, file }) => {
      const formData = new FormData()
      formData.append('file', file)

      const response = await apiClient.post<RohsComponentComplianceFileDetailView>(
        `/v1/catalog/components/${componentId}/rohs/compliance-file/slots/${slotKind}`,
        formData
      )
      return response.data
    },
    onSuccess: () => {
      invalidateCatalogComponentComplianceQueries(queryClient, componentId)
    },
  })
}

export function useCompleteCatalogRohsComponentComplianceFile(componentId: string) {
  const queryClient = useQueryClient()

  return useMutation<CompleteRohsComponentComplianceFileResult, Error, void>({
    mutationFn: async () => {
      const response =
        await apiClient.post<CompleteRohsComponentComplianceFileResult>(
          `/v1/catalog/components/${componentId}/rohs/compliance-file/complete`
        )
      return response.data
    },
    onSuccess: () => {
      invalidateCatalogComponentComplianceQueries(queryClient, componentId)
    },
  })
}

export function useUncompleteCatalogRohsComponentComplianceFile(
  componentId: string
) {
  const queryClient = useQueryClient()

  return useMutation<
    { file: RohsComponentComplianceFileDetailView },
    Error,
    void
  >({
    mutationFn: async () => {
      const response = await apiClient.post<{
        file: RohsComponentComplianceFileDetailView
      }>(`/v1/catalog/components/${componentId}/rohs/compliance-file/uncomplete`)
      return response.data
    },
    onSuccess: () => {
      invalidateCatalogComponentComplianceQueries(queryClient, componentId)
    },
  })
}

const PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS =
  180_000

type RohsProductComplianceFileDeclarationOfConformityStreamEvent =
  | { type: 'workflow.started' }
  | { type: 'step.started'; step: string }
  | { type: 'step.completed'; step: string }
  | { type: 'progress'; message: string; temporary?: boolean }
  | {
      type: 'workflow.completed'
      result?: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }
    }
  | { type: 'error'; error: string }
  | { type: 'workflow.failed'; error?: string }

function getApiStreamUrl(path: string): string {
  const baseURL = apiClient.defaults.baseURL ?? ''
  return `${baseURL}${path}`
}

function getApiStreamHeaders(): Record<string, string> {
  const common = (apiClient.defaults.headers.common ?? {}) as Record<
    string,
    string
  >
  const { 'Content-Type': _contentType, ...rest } = common
  return rest
}

async function downloadRohsProductComplianceFileOutputDocumentFromUrl(
  downloadUrl: string,
  fileName: string
) {
  const response = await fetch(downloadUrl)
  if (!response.ok) {
    throw new Error(`Download failed: ${response.statusText}`)
  }
  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = fileName
  link.click()
  URL.revokeObjectURL(objectUrl)
}

export function useDownloadRohsProductComplianceFileOutputDocument(
  productId: string
) {
  return useMutation<
    void,
    Error,
    { outputDocumentId: string; fileName?: string }
  >({
    mutationFn: async ({ outputDocumentId, fileName }) => {
      const response = await apiClient.get<{
        downloadUrl: string
        fileName: string
        declarationNumber: string | null
      }>(
        `/v2/products/${productId}/rohs/compliance-file/output-documents/${outputDocumentId}/download-url`
      )
      await downloadRohsProductComplianceFileOutputDocumentFromUrl(
        response.data.downloadUrl,
        fileName ?? response.data.fileName
      )
    },
  })
}

export function useStartRohsProductComplianceFileDeclarationOfConformityGeneration(
  productId: string
) {
  const queryClient = useQueryClient()

  const invalidateFiles = () => {
    void queryClient.invalidateQueries({
      queryKey: [PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
    })
  }

  return useMutation<
    void,
    Error,
    {
      manufacturerName: string
      manufacturerAddress: string
      onEvent?: (
        event: RohsProductComplianceFileDeclarationOfConformityStreamEvent
      ) => void
      onComplete?: (result: {
        outputDocumentId: string
        declarationNumber: string
        fileName: string
      }) => void
    }
  >({
    mutationFn: async ({
      manufacturerName,
      manufacturerAddress,
      onEvent,
      onComplete,
    }) => {
      const url = getApiStreamUrl(
        `/v2/products/${productId}/rohs/compliance-file/declaration-of-conformity/generate`
      )
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getApiStreamHeaders(),
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          manufacturerName,
          manufacturerAddress,
        }),
        signal: AbortSignal.timeout(
          PRODUCT_COMPLIANCE_FILE_DECLARATION_OF_CONFORMITY_GENERATE_TIMEOUT_MS
        ),
      })
      if (!response.ok) {
        throw new Error(
          `Failed to start Declaration of Conformity generation: ${response.statusText}`
        )
      }
      if (!response.body) {
        throw new Error('Response body is null')
      }

      invalidateFiles()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let hasInvalidatedOnProgress = false

      try {
        while (true) {
          const { done, value } = await reader.read()
          if (done) {
            break
          }
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) {
              continue
            }
            const data = line.slice(6).trim()
            if (!data) {
              continue
            }
            const event = JSON.parse(
              data
            ) as RohsProductComplianceFileDeclarationOfConformityStreamEvent & {
              type: string
            }
            if (
              !hasInvalidatedOnProgress &&
              (event.type === 'workflow.started' ||
                event.type === 'step.started' ||
                event.type === 'progress')
            ) {
              hasInvalidatedOnProgress = true
              invalidateFiles()
            }
            if (
              event.type === 'progress' ||
              event.type === 'step.started' ||
              event.type === 'step.completed' ||
              event.type === 'error'
            ) {
              onEvent?.(event)
            }
            if (event.type === 'workflow.completed') {
              const completedEvent = event as Extract<
                RohsProductComplianceFileDeclarationOfConformityStreamEvent,
                { type: 'workflow.completed' }
              >
              if (completedEvent.result) {
                await onComplete?.(completedEvent.result)
              }
              return
            }
            if (event.type === 'error') {
              throw new Error(event.error ?? 'Generation failed')
            }
            if (event.type === 'workflow.failed') {
              throw new Error(
                (event as { error?: string }).error ?? 'Workflow failed'
              )
            }
          }
        }
      } finally {
        reader.releaseLock()
      }
    },
    onSuccess: () => {
      invalidateFiles()
    },
    onError: () => {
      invalidateFiles()
    },
  })
}
